import React, { useState } from 'react'
import {
  Brain, Send, CheckCircle, XCircle, AlertTriangle, Lightbulb,
  ArrowRight, History, ChevronDown, ChevronUp, Star, Target,
  BarChart2, BookOpen, RefreshCw, Award, Hash, Divide
} from 'lucide-react'
import MisconceptionAlert from '../components/MisconceptionAlert'
import OntologyTag from '../components/OntologyTag'
import { useProgressStore } from '../store/progressStore'
import type { TutorAnalysisResult } from '../types'

// ────────────────────────────────────────────────────────────────────────────
// 소인수분해 단원 데모 시나리오 (NM01~NM12)
// ────────────────────────────────────────────────────────────────────────────
const DEMO_SCENARIOS = [
  {
    label: '📌 시나리오 1: 1을 소수로 오해 (NM01)',
    emoji: '📌',
    category: '소수와 합성수',
    step: 'NS01',
    question: '다음 중 소수를 모두 고르시오: 1, 2, 3, 5, 9',
    answer: '1, 2, 3, 5',
    wrongAnswer: '1, 2, 3, 5',
    note: '1을 소수에 포함시킨 경우',
  },
  {
    label: '📌 시나리오 2: 2가 합성수라는 오해 (NM02)',
    emoji: '📌',
    category: '소수와 합성수',
    step: 'NS01',
    question: '짝수는 모두 합성수인가요?',
    answer: '네, 짝수는 2로 나누어지니 모두 합성수입니다.',
    wrongAnswer: '네, 짝수는 2로 나누어지니 모두 합성수입니다.',
    note: '2가 유일한 짝수 소수임을 모르는 경우',
  },
  {
    label: '📌 시나리오 3: 지수를 곱셈으로 오해 (NM03)',
    emoji: '📌',
    category: '거듭제곱과 지수',
    step: 'NS02',
    question: '2³의 값은 얼마입니까?',
    answer: '2³ = 2 × 3 = 6',
    wrongAnswer: '2³ = 2 × 3 = 6',
    note: '2³을 2×3=6으로 계산한 경우',
  },
  {
    label: '📌 시나리오 4: 합성수로 소인수분해 종료 (NM05)',
    emoji: '📌',
    category: '소인수분해 방법',
    step: 'NS03',
    question: '36을 소인수분해하시오.',
    answer: '36 = 4 × 9',
    wrongAnswer: '36 = 4 × 9',
    note: '소수가 아닌 4와 9로 분해를 끝낸 경우',
  },
  {
    label: '📌 시나리오 5: GCD에서 큰 지수 선택 (NM07)',
    emoji: '📌',
    category: '최대공약수(GCD)',
    step: 'NS04',
    question: '12 = 2² × 3, 18 = 2 × 3²일 때 GCD(12, 18)을 구하시오.',
    answer: 'GCD = 2² × 3² = 36',
    wrongAnswer: 'GCD = 2² × 3² = 36',
    note: 'GCD에서 공통 소인수의 큰 지수를 선택한 경우',
  },
  {
    label: '✅ 시나리오 6: 올바른 LCM 계산',
    emoji: '✅',
    category: '최소공배수(LCM)',
    step: 'NS05',
    question: '18 = 2 × 3², 30 = 2 × 3 × 5일 때 LCM(18, 30)을 구하시오.',
    answer: 'LCM = 2 × 3² × 5 = 90',
    wrongAnswer: 'LCM = 2 × 3² × 5 = 90',
    note: '정확한 답변',
  },
]

// 시나리오별 결과 매핑 키
const SCENARIO_KEYS = [
  'NS01-wrong-nm01', 'NS01-wrong-nm02', 'NS02-wrong-nm03',
  'NS03-wrong-nm05', 'NS04-wrong-nm07', 'NS05-correct',
]

// ────────────────────────────────────────────────────────────────────────────
// 소인수분해 단원 데모 결과 데이터
// ────────────────────────────────────────────────────────────────────────────
const DEMO_RESULTS: Record<string, TutorAnalysisResult> = {
  'NS01-wrong-nm01': {
    student_id: 'student_001', current_step: 'NS01', step_concept: '소수와 합성수', score: 20,
    mastery: { step_id: 'NS01', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '소수의 정의를 다시 학습해야 합니다.' },
    misconceptions: [{
      id: 'NM01', type: 'ONE_IS_PRIME', severity: 'high',
      description: 'Treating 1 as a prime number',
      description_ko: '1을 소수로 잘못 인식하는 오류',
      feedback_id: 'F-NM01', learning_step: 'NS01',
    }],
    concept_gaps: [{ concept: 'PrimeNumber', from_misconception: 'NM01', severity: 'high', remedial_step: 'NS01' }],
    feedbacks: [{
      id: 'F-NM01', misconception: 'NM01',
      title: '1은 소수가 아닙니다!',
      message: '소수는 약수가 정확히 2개인 수입니다. 1의 약수는 1뿐(1개)이므로 소수 정의에 맞지 않습니다. 소수: 2, 3, 5, 7, 11, ... (약수 딱 2개). 1은 소수도 합성수도 아닌 특별한 수입니다.',
      hint: '소수 = 약수가 정확히 2개 (1과 자기 자신). 1은 약수가 1개뿐이라 소수 ✗',
      next_step: 'NS01 소수 정의 재학습',
    }],
    primary_feedback: {
      id: 'F-NM01', misconception: 'NM01',
      title: '1은 소수가 아닙니다!',
      message: '소수의 정의: 약수가 정확히 2개인 자연수. 1의 약수는 {1}으로 1개뿐이므로 소수가 아닙니다. 정답: 2, 3, 5',
      hint: '소수 판별법: 약수 개수가 정확히 2개인지 확인하세요!',
      next_step: 'NS01 소수 정의 재학습',
    },
    next_step_recommendation: {
      current_step: 'NS01', next_step: 'NS01',
      recommendation: 'reinforce', reason: 'NM01 오개념 해결 후 소수 개념 재학습 필요', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['PrimeNumber', 'Divisor', 'NaturalNumber', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 1을 소수 목록에 포함시켰습니다.',
      reasoned: 'NM01(1을 소수로 오인) 오개념이 감지됩니다. 소수의 정의(약수 2개)를 모르거나 혼동하고 있습니다.',
      planned: 'NM01 피드백으로 소수 정의를 명확히 하고 NS01 재학습 권장',
      acted: '약수 개수를 기준으로 소수를 판별하는 방법을 단계별로 안내합니다.',
    },
  },

  'NS01-wrong-nm02': {
    student_id: 'student_001', current_step: 'NS01', step_concept: '소수와 합성수', score: 30,
    mastery: { step_id: 'NS01', score: 30, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '2는 유일한 짝수 소수입니다. 다시 확인하세요.' },
    misconceptions: [{
      id: 'NM02', type: 'TWO_IS_COMPOSITE', severity: 'medium',
      description: 'Thinking all even numbers are composite',
      description_ko: '모든 짝수를 합성수로 오해하는 오류',
      feedback_id: 'F-NM02', learning_step: 'NS01',
    }],
    concept_gaps: [{ concept: 'PrimeNumber', from_misconception: 'NM02', severity: 'medium', remedial_step: 'NS01' }],
    feedbacks: [{
      id: 'F-NM02', misconception: 'NM02',
      title: '2는 유일한 짝수 소수입니다!',
      message: '짝수 = 2의 배수이지만, 2 자체는 약수가 1과 2, 딱 2개입니다. 따라서 2는 소수입니다! 2를 제외한 모든 짝수는 2를 약수로 가지므로 합성수가 맞습니다.',
      hint: '2는 짝수이지만 소수! 소수 중 유일한 짝수입니다.',
      next_step: 'NS01 소수·합성수 구분 재학습',
    }],
    primary_feedback: {
      id: 'F-NM02', misconception: 'NM02',
      title: '2는 유일한 짝수 소수입니다!',
      message: '2의 약수: 1, 2 → 약수 2개 → 소수 ✓. 짝수 중 소수는 2뿐입니다. "짝수 = 합성수"라는 규칙은 2에는 적용되지 않습니다.',
      hint: '소수 2는 특별 케이스! 유일한 짝수 소수로 암기하세요.',
      next_step: 'NS01 재학습',
    },
    next_step_recommendation: {
      current_step: 'NS01', next_step: 'NS01',
      recommendation: 'reinforce', reason: 'NM02 오개념 해결 후 소수 목록 재확인 필요', score: 30, misconception_count: 1,
    },
    ontology_classes_activated: ['PrimeNumber', 'EvenNumber', 'Divisor', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 모든 짝수는 합성수라고 답했습니다.',
      reasoned: 'NM02(2가 합성수라는 오해) 오개념이 감지됩니다.',
      planned: 'NM02 피드백으로 2가 유일한 짝수 소수임을 강조',
      acted: '2의 약수를 직접 확인하는 방법으로 납득시킵니다.',
    },
  },

  'NS02-wrong-nm03': {
    student_id: 'student_001', current_step: 'NS02', step_concept: '거듭제곱과 지수', score: 15,
    mastery: { step_id: 'NS02', score: 15, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '지수의 의미를 다시 학습하세요.' },
    misconceptions: [{
      id: 'NM03', type: 'EXPONENT_AS_MULTIPLY', severity: 'high',
      description: 'Confusing exponent with multiplication (2³ = 2×3)',
      description_ko: '지수를 곱셈으로 혼동하는 오류 (2³을 2×3=6으로 계산)',
      feedback_id: 'F-NM03', learning_step: 'NS02',
    }],
    concept_gaps: [{ concept: 'Exponent', from_misconception: 'NM03', severity: 'high', remedial_step: 'NS02' }],
    feedbacks: [{
      id: 'F-NM03', misconception: 'NM03',
      title: '지수는 곱하는 횟수입니다!',
      message: '2³은 "2를 3번 곱한다"는 의미입니다. 2³ = 2 × 2 × 2 = 8. 절대 2 × 3 = 6이 아닙니다! 지수(위첨자)는 밑수를 몇 번 곱하는지를 나타냅니다.',
      hint: 'aⁿ = a를 n번 곱하기. 2³ = 2×2×2 = 8 (2+3도 아니고 2×3도 아님!)',
      next_step: 'NS02 거듭제곱 개념 재학습',
    }],
    primary_feedback: {
      id: 'F-NM03', misconception: 'NM03',
      title: '2³ ≠ 6, 2³ = 8!',
      message: '지수는 밑수를 반복 곱하는 횟수입니다. 2³ = 2 × 2 × 2 = 8. "2를 3으로 곱한다(2×3)"가 아니라 "2를 3번 곱한다(2×2×2)"입니다.',
      hint: '3² = 3×3 = 9, 2⁴ = 2×2×2×2 = 16, 5² = 5×5 = 25',
      next_step: 'NS02 거듭제곱 재학습',
    },
    next_step_recommendation: {
      current_step: 'NS02', next_step: 'NS02',
      recommendation: 'reinforce', reason: 'NM03 오개념 해결 후 거듭제곱 재학습', score: 15, misconception_count: 1,
    },
    ontology_classes_activated: ['Exponent', 'Base', 'PrimePower', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 2³을 2×3=6으로 계산했습니다.',
      reasoned: 'NM03(지수를 곱셈으로 혼동) 오개념이 강하게 감지됩니다.',
      planned: 'NM03 피드백으로 지수의 의미를 반복 곱셈으로 명확히 설명',
      acted: '구체적 예시(2³=2×2×2=8)로 시각적으로 이해시킵니다.',
    },
  },

  'NS03-wrong-nm05': {
    student_id: 'student_001', current_step: 'NS03', step_concept: '소인수분해 방법', score: 25,
    mastery: { step_id: 'NS03', score: 25, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '소인수분해는 소수만의 곱으로 나타내야 합니다.' },
    misconceptions: [{
      id: 'NM05', type: 'COMPOSITE_FACTOR_REMAIN', severity: 'high',
      description: 'Stopping factorization at composite numbers',
      description_ko: '합성수가 남아있는데 소인수분해를 끝내는 오류',
      feedback_id: 'F-NM05', learning_step: 'NS03',
    }],
    concept_gaps: [{ concept: 'PrimeFactorization', from_misconception: 'NM05', severity: 'high', remedial_step: 'NS03' }],
    feedbacks: [{
      id: 'F-NM05', misconception: 'NM05',
      title: '소인수분해는 소수만 남아야 합니다!',
      message: '36 = 4 × 9는 소인수분해가 아닙니다! 4와 9는 소수가 아닙니다. 계속 분해해야 합니다: 4 = 2², 9 = 3². 따라서 36 = 2² × 3²이 올바른 소인수분해입니다.',
      hint: '모든 인수가 소수인지 확인! 소수인 것: 2, 3, 5, 7, 11... 합성수인 것: 4, 6, 8, 9, 10...',
      next_step: 'NS03 소인수분해 방법 재학습',
    }],
    primary_feedback: {
      id: 'F-NM05', misconception: 'NM05',
      title: '36 = 4×9 ✗, 36 = 2²×3² ✓',
      message: '소인수분해 결과는 반드시 소수들의 곱이어야 합니다. 4(=2²)와 9(=3²)는 소수가 아니므로 더 분해해야 합니다. 36 = 4×9 = 2²×3² = 2×2×3×3',
      hint: '소인수분해 완료 체크: 모든 인수가 소수(2,3,5,7...)인가요?',
      next_step: 'NS03 재학습',
    },
    next_step_recommendation: {
      current_step: 'NS03', next_step: 'NS03',
      recommendation: 'reinforce', reason: 'NM05 오개념 해결 후 소인수분해 절차 재학습', score: 25, misconception_count: 1,
    },
    ontology_classes_activated: ['PrimeFactorization', 'PrimeNumber', 'CompositeNumber', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 36을 4×9로 소인수분해했다고 답했습니다.',
      reasoned: 'NM05(합성수가 남은 채로 소인수분해 종료) 오개념이 감지됩니다.',
      planned: 'NM05 피드백으로 소인수분해의 완료 조건(모든 인수가 소수)을 강조',
      acted: '4=2², 9=3²을 추가 분해하는 과정을 단계별로 보여줍니다.',
    },
  },

  'NS04-wrong-nm07': {
    student_id: 'student_001', current_step: 'NS04', step_concept: '최대공약수(GCD)', score: 20,
    mastery: { step_id: 'NS04', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: 'GCD에서는 공통 소인수의 작은 지수를 선택합니다.' },
    misconceptions: [{
      id: 'NM07', type: 'GCD_LARGE_EXPONENT', severity: 'high',
      description: 'Choosing larger exponent when computing GCD',
      description_ko: 'GCD 계산 시 공통 소인수의 큰 지수를 선택하는 오류',
      feedback_id: 'F-NM07', learning_step: 'NS04',
    }],
    concept_gaps: [{ concept: 'GCD', from_misconception: 'NM07', severity: 'high', remedial_step: 'NS04' }],
    feedbacks: [{
      id: 'F-NM07', misconception: 'NM07',
      title: 'GCD는 작은 지수, LCM은 큰 지수!',
      message: 'GCD(최대공약수)는 두 수의 공통 약수 중 가장 큰 것입니다. 공통 소인수에서 작은 지수를 선택해야 합니다. 12 = 2²×3, 18 = 2×3²에서 GCD = 2¹×3¹ = 6. 큰 지수를 선택하면 LCM(최소공배수)이 됩니다!',
      hint: 'GCD → 공통 소인수 × 작은 지수 / LCM → 모든 소인수 × 큰 지수',
      next_step: 'NS04 GCD 계산법 재학습',
    }],
    primary_feedback: {
      id: 'F-NM07', misconception: 'NM07',
      title: 'GCD(12,18) = 6, not 36!',
      message: '12 = 2²×3, 18 = 2×3². GCD는 공통 소인수(2와 3)의 작은 지수 선택: 2^min(2,1) × 3^min(1,2) = 2¹×3¹ = 6. 큰 지수(2²×3²=36)는 LCM입니다.',
      hint: 'GCD: min 지수 선택 → 6 / LCM: max 지수 선택 → 36',
      next_step: 'NS04 재학습',
    },
    next_step_recommendation: {
      current_step: 'NS04', next_step: 'NS04',
      recommendation: 'reinforce', reason: 'NM07 오개념 해결 후 GCD 계산법 재학습', score: 20, misconception_count: 1,
    },
    ontology_classes_activated: ['GCD', 'PrimeFactorization', 'Exponent', 'LCM', 'Misconception', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 GCD 계산에서 공통 소인수의 큰 지수를 선택하여 36을 답했습니다.',
      reasoned: 'NM07(GCD에서 큰 지수 선택) 오개념이 감지됩니다. GCD와 LCM의 지수 선택 규칙을 혼동하고 있습니다.',
      planned: 'NM07 피드백으로 GCD=작은지수, LCM=큰지수 규칙을 명확히 대조 설명',
      acted: '구체적 계산 과정으로 min/max 지수 선택의 차이를 보여줍니다.',
    },
  },

  'NS05-correct': {
    student_id: 'student_001', current_step: 'NS05', step_concept: '최소공배수(LCM)', score: 100,
    mastery: { step_id: 'NS05', score: 100, attempts: 1, mastered: true, near_mastery: false, level: 'mastered', message: '완벽합니다! LCM 계산을 마스터했습니다.' },
    misconceptions: [],
    concept_gaps: [],
    feedbacks: [{
      id: 'F-correct', type: 'encouragement',
      title: '훌륭해요! 🎉',
      message: 'LCM(18, 30) = 2 × 3² × 5 = 90을 정확히 구했습니다! 모든 소인수(2, 3, 5)를 포함하고 각각의 가장 큰 지수(2¹, 3², 5¹)를 선택했습니다.',
      emoji: '🌟',
      personalized_message: '완벽한 답변입니다! GCD·LCM 단원도 도전해보세요.',
    }],
    primary_feedback: {
      id: 'F-correct', type: 'encouragement',
      title: '훌륭해요! 🎉',
      message: 'LCM = 2 × 3² × 5 = 90을 정확히 구했습니다!',
      emoji: '🌟',
    },
    next_step_recommendation: {
      current_step: 'NS05', next_step: 'NS06',
      recommendation: 'advance', reason: 'LCM 마스터! GCD·LCM 활용 학습으로 진행', score: 100, misconception_count: 0,
    },
    ontology_classes_activated: ['LCM', 'PrimeFactorization', 'Exponent', 'Feedback'],
    agent_reasoning: {
      perceived: '학생이 LCM(18,30)=90을 정확하게 계산했습니다.',
      reasoned: '오개념이 없습니다. 완전 학습 상태입니다.',
      planned: '격려 피드백 후 NS06 GCD·LCM 활용으로 진행',
      acted: '칭찬과 함께 다음 단계(활용 문제)를 안내합니다.',
    },
  },
}

// ────────────────────────────────────────────────────────────────────────────
// 세션 히스토리 타입
// ────────────────────────────────────────────────────────────────────────────
interface HistoryEntry {
  scenarioLabel: string
  score: number
  grade: string
  misconceptionCount: number
  at: string
}

function getGradeFromScore(score: number): string {
  if (score >= 90) return 'A'
  if (score >= 70) return 'B'
  if (score >= 50) return 'C'
  return 'D'
}

// 단계별 색상 매핑
const STEP_COLORS: Record<string, string> = {
  NS01: 'bg-blue-100 text-blue-700',
  NS02: 'bg-purple-100 text-purple-700',
  NS03: 'bg-green-100 text-green-700',
  NS04: 'bg-orange-100 text-orange-700',
  NS05: 'bg-red-100 text-red-700',
  NS06: 'bg-teal-100 text-teal-700',
}

// ────────────────────────────────────────────────────────────────────────────
// 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
export default function N1TutorPage() {
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [customAnswer, setCustomAnswer] = useState('')
  const [result, setResult] = useState<TutorAnalysisResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [showReasoning, setShowReasoning] = useState(false)
  const [showOntology, setShowOntology] = useState(false)

  const { recordMisconceptions } = useProgressStore(s => ({
    recordMisconceptions: s.recordMisconceptions,
  }))

  const scenario = DEMO_SCENARIOS[selectedIdx]

  const handleAnalyze = async () => {
    setLoading(true)
    setResult(null)
    await new Promise(r => setTimeout(r, 900))
    const key = SCENARIO_KEYS[selectedIdx]
    const res = DEMO_RESULTS[key]
    setResult(res)

    // 오개념 progressStore에 기록
    if (res.misconceptions.length > 0) {
      recordMisconceptions('N1-1', res.misconceptions.map(m => m.id))
    }

    // 세션 히스토리 추가
    setHistory(prev => [
      {
        scenarioLabel: scenario.label,
        score: res.score,
        grade: getGradeFromScore(res.score),
        misconceptionCount: res.misconceptions.length,
        at: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
      },
      ...prev,
    ].slice(0, 10))

    setLoading(false)
  }

  const recColor =
    result?.next_step_recommendation.recommendation === 'advance'
      ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
      : result?.next_step_recommendation.recommendation === 'reinforce'
        ? 'text-blue-700 bg-blue-50 border border-blue-200'
        : 'text-orange-700 bg-orange-50 border border-orange-200'

  const avgScore = history.length > 0
    ? Math.round(history.reduce((s, h) => s + h.score, 0) / history.length)
    : null

  return (
    <div className="p-6 space-y-5">
      {/* ── 헤더 ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Brain size={22} className="text-purple-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">AI 튜터 — 답안 분석</h1>
            <p className="text-sm text-slate-500">소인수분해(N1-1) 오개념을 감지하고 맞춤 피드백을 제공합니다</p>
          </div>
        </div>
        {avgScore !== null && (
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-xl">
            <Star size={13} className="text-purple-500" />
            <span className="text-xs font-bold text-purple-700">세션 평균 {avgScore}점</span>
          </div>
        )}
      </div>

      {/* ── 단원 개요 배너 ─────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <Hash size={16} />
          <span className="font-bold text-sm">소인수분해 단원 주요 오개념</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { code: 'NM01', label: '1≠소수', step: 'NS01' },
            { code: 'NM02', label: '2=짝수소수', step: 'NS01' },
            { code: 'NM03', label: '지수≠곱셈', step: 'NS02' },
            { code: 'NM05', label: '소수만의 곱', step: 'NS03' },
            { code: 'NM07', label: 'GCD=작은지수', step: 'NS04' },
            { code: 'NM08', label: 'LCM=큰지수', step: 'NS05' },
          ].map(m => (
            <span key={m.code} className="text-xs bg-white/20 px-2 py-1 rounded-lg font-medium">
              {m.code}: {m.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── 세션 기록 토글 ──────────────────────────────────────────────── */}
      {history.length > 0 && (
        <div className="card border border-slate-200">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors"
          >
            <span className="flex items-center gap-2">
              <History size={14} />
              세션 기록 ({history.length}회)
            </span>
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-1.5">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-xs py-1 border-b border-slate-50 last:border-0">
                  <span className="text-slate-400 w-10">{h.at}</span>
                  <span className="flex-1 text-slate-600 truncate">{h.scenarioLabel.replace(/^📌|✅\s/, '')}</span>
                  <span className={`font-black w-12 text-right ${h.score >= 70 ? 'text-emerald-600' : h.score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                    {h.score}점
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${h.score >= 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {h.grade}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 2열 레이아웃: 입력 | 결과 ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── 왼쪽: 시나리오 선택 + 답안 입력 ───────────────────────────── */}
        <div className="space-y-4">
          {/* 시나리오 목록 */}
          <div className="card">
            <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <Target size={14} className="text-indigo-500" />
              소인수분해 오개념 시나리오
              <span className="ml-auto text-xs text-slate-400 font-normal">({DEMO_SCENARIOS.length}가지)</span>
            </p>
            <div className="space-y-1.5">
              {DEMO_SCENARIOS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedIdx(i); setResult(null); setCustomAnswer('') }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-start gap-2 ${
                    selectedIdx === i
                      ? 'bg-indigo-100 text-indigo-700 font-semibold shadow-sm'
                      : 'hover:bg-slate-50 text-slate-600'
                  }`}
                >
                  <span className="text-base leading-none mt-0.5">{s.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{s.category}</div>
                    <div className="text-slate-400 font-normal mt-0.5 truncate">{s.note}</div>
                  </div>
                  <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[s.step] || 'bg-slate-100 text-slate-600'}`}>
                    {s.step}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* 문제 + 답안 입력 */}
          <div className="card space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <BookOpen size={11} />
                문제
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[scenario.step] || 'bg-slate-100 text-slate-600'}`}>
                  {scenario.step}
                </span>
              </p>
              <p className="text-sm text-slate-800 bg-slate-50 rounded-xl p-3 border border-slate-200 leading-relaxed">
                {scenario.question}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1">학생 답안 (수정 가능)</p>
              <textarea
                className="w-full h-20 resize-none border border-slate-200 rounded-xl p-3 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all"
                value={customAnswer || scenario.wrongAnswer}
                onChange={e => setCustomAnswer(e.target.value)}
                placeholder="답안을 입력하세요..."
              />
              <p className="text-xs text-slate-400 mt-1">
                💡 답안을 직접 수정해보면 다양한 오개념을 실험할 수 있습니다.
              </p>
            </div>
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> AI 분석 중...</>
              ) : (
                <><Send size={16} /> AI 튜터 분석 시작</>
              )}
            </button>
          </div>
        </div>

        {/* ── 오른쪽: 분석 결과 ─────────────────────────────────────────── */}
        <div className="space-y-4">
          {!result && !loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center bg-gradient-to-b from-indigo-50 to-white">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
                <Divide size={32} className="text-indigo-400" />
              </div>
              <p className="text-slate-500 text-sm font-semibold">소인수분해 시나리오를 선택하고</p>
              <p className="text-slate-400 text-sm">"AI 튜터 분석 시작"을 눌러보세요</p>
              <div className="mt-4 text-xs text-slate-300">
                NM01~NM12 오개념 감지 · 단계별 피드백 · 다음 학습 추천
              </div>
            </div>
          )}

          {loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center">
              <div className="relative mb-4">
                <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
                <Brain size={20} className="absolute inset-0 m-auto text-indigo-400" />
              </div>
              <p className="text-slate-600 font-semibold">AI 분석 중...</p>
              <p className="text-slate-400 text-xs mt-1">소인수분해 오개념 패턴 매칭 중...</p>
            </div>
          )}

          {result && (
            <>
              {/* 점수 카드 */}
              <div className={`card flex items-center gap-4 ${result.score >= 70 ? 'bg-emerald-50 border border-emerald-200' : result.score >= 40 ? 'bg-orange-50 border border-orange-200' : 'bg-red-50 border border-red-200'}`}>
                {result.score >= 70
                  ? <CheckCircle size={36} className="text-emerald-500 flex-shrink-0" />
                  : <XCircle size={36} className="text-red-500 flex-shrink-0" />}
                <div className="flex-1">
                  <div className="text-3xl font-black text-slate-800">{result.score}점</div>
                  <div className="text-sm text-slate-600 mt-0.5">{result.mastery.message}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      result.mastery.mastered ? 'bg-emerald-100 text-emerald-700' :
                      result.mastery.near_mastery ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {result.mastery.level === 'mastered' ? '✓ 마스터' :
                       result.mastery.level === 'near_mastery' ? '거의 완료' : '학습 중'}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-md font-bold ${STEP_COLORS[result.current_step] || 'bg-slate-100 text-slate-600'}`}>
                      {result.current_step}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-black ${result.score >= 70 ? 'text-emerald-500' : result.score >= 40 ? 'text-orange-500' : 'text-red-500'}`}>
                    {getGradeFromScore(result.score)}
                  </div>
                  <div className="text-xs text-slate-400">등급</div>
                </div>
              </div>

              {/* 오개념 */}
              {result.misconceptions.length > 0 && (
                <div className="card">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={15} className="text-orange-500" />
                    <p className="text-sm font-bold text-slate-700">감지된 오개념</p>
                    <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">
                      {result.misconceptions.length}건
                    </span>
                  </div>
                  <div className="space-y-3">
                    {result.misconceptions.map(m => (
                      <MisconceptionAlert
                        key={m.id}
                        misconception={m}
                        feedback={result.feedbacks.find(f => f.id === m.feedback_id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 주요 피드백 */}
              {result.primary_feedback && (
                <div className="card bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <Lightbulb size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-slate-800">{result.primary_feedback.title}</p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed">{result.primary_feedback.message}</p>
                      {result.primary_feedback.hint && (
                        <div className="mt-2 text-xs text-amber-800 bg-amber-100 rounded-xl px-3 py-2 border border-amber-200">
                          💡 <span className="font-semibold">힌트:</span> {result.primary_feedback.hint}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 다음 학습 추천 */}
              <div className={`card flex items-center gap-3 ${recColor}`}>
                <ArrowRight size={18} className="flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">다음 학습 단계 추천</p>
                  <p className="text-sm font-bold">{result.next_step_recommendation.next_step}</p>
                  <p className="text-xs opacity-80 mt-0.5">{result.next_step_recommendation.reason}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-lg font-bold ${
                  result.next_step_recommendation.recommendation === 'advance'
                    ? 'bg-emerald-200 text-emerald-800'
                    : 'bg-blue-200 text-blue-800'
                }`}>
                  {result.next_step_recommendation.recommendation === 'advance' ? '↑ 진행' : '↩ 복습'}
                </span>
              </div>

              {/* Agentic AI 추론 과정 (토글) */}
              <div className="card">
                <button
                  onClick={() => setShowReasoning(!showReasoning)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <BarChart2 size={14} />
                    🤖 Agentic AI 추론 과정
                  </span>
                  {showReasoning ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showReasoning && (
                  <div className="mt-3 space-y-2 text-xs text-slate-600">
                    {[
                      { label: '인식', value: result.agent_reasoning.perceived },
                      { label: '추론', value: result.agent_reasoning.reasoned },
                      { label: '계획', value: result.agent_reasoning.planned },
                      { label: '행동', value: result.agent_reasoning.acted },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex gap-2">
                        <span className="font-bold text-slate-500 w-8 flex-shrink-0">{label}</span>
                        <span className="text-slate-600">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* OWL 클래스 (토글) */}
              <div className="card">
                <button
                  onClick={() => setShowOntology(!showOntology)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Award size={14} />
                    활성화된 OWL 클래스
                  </span>
                  {showOntology ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showOntology && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {result.ontology_classes_activated.map(c => (
                      <OntologyTag key={c} className_name={c} />
                    ))}
                  </div>
                )}
              </div>

              {/* 다시 시도 버튼 */}
              <button
                onClick={() => { setResult(null); setCustomAnswer('') }}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-indigo-600 border border-slate-200 rounded-xl py-2 hover:border-indigo-300 transition-colors"
              >
                <RefreshCw size={13} /> 다른 시나리오 선택
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
